import React, { useState } from 'react';
import { Check, Edit, Star } from 'lucide-react';

interface ColorPickerSectionProps {
  primaryColor: string;
  setPrimaryColor: (color: string) => void;
  secondaryColor: string;
  setSecondaryColor: (color: string) => void;
  businessName: string;
}

// Paletas de colores predefinidas con combinaciones primario/secundario sugeridas
const COLOR_PRESETS = [
  { primary: '#006241', secondary: '#D4E9E2', label: 'Verde Bosque' },
  { primary: '#1a4bff', secondary: '#e6ebff', label: 'Azul Royal' },
  { primary: '#E31837', secondary: '#FDE8EB', label: 'Rojo Vibrante' },
  { primary: '#FF6900', secondary: '#FFF0E5', label: 'Naranja Energía' },
  { primary: '#7C3AED', secondary: '#EDE9FE', label: 'Morado Elegante' },
  { primary: '#0891B2', secondary: '#E0F7FA', label: 'Turquesa' },
  { primary: '#BE185D', secondary: '#FCE7F3', label: 'Rosa Fuerte' },
  { primary: '#1F2937', secondary: '#F3F4F6', label: 'Gris Ejecutivo' },
];

// Función para determinar si un color es claro u oscuro
const isLightColor = (hex: string): boolean => {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6;
};

const ColorPickerSection: React.FC<ColorPickerSectionProps> = ({
  primaryColor,
  setPrimaryColor,
  secondaryColor,
  setSecondaryColor,
  businessName,
}) => {
  const [showCustomPrimary, setShowCustomPrimary] = useState(false);
  const [showCustomSecondary, setShowCustomSecondary] = useState(false);


  const handlePresetClick = (preset: typeof COLOR_PRESETS[0]) => {
    setPrimaryColor(preset.primary);
    setSecondaryColor(preset.secondary);
    setShowCustomPrimary(false);
    setShowCustomSecondary(false);
  };

  const primaryTextColor = isLightColor(primaryColor) ? '#1e293b' : '#ffffff';

  return (
    <div>
      <label className="block text-sm font-medium text-content-2 mb-3">
        Colores del Negocio
      </label>

      {/* Presets rápidos */}
      <div className="mb-4">
        <p className="text-xs text-content-3 mb-2">Elige una combinación predefinida o personaliza tus colores:</p>
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
          {COLOR_PRESETS.map((preset) => {
            const isSelected = preset.primary === primaryColor && preset.secondary === secondaryColor;
            return (
              <button
                key={preset.primary}
                type="button"
                onClick={() => handlePresetClick(preset)}
                className={`group relative h-10 rounded-lg border-2 transition-all overflow-hidden ${
                  isSelected 
                    ? 'border-content ring-2 ring-offset-1 ring-offset-surface ring-accent scale-105'
                    : 'border-line hover:border-content-3 hover:scale-105'
                }`}
                title={preset.label}
              >
                {/* Mitad superior: color primario */}
                <div className="absolute inset-x-0 top-0 h-1/2" style={{ backgroundColor: preset.primary }} />
                {/* Mitad inferior: color secundario */}
                <div className="absolute inset-x-0 bottom-0 h-1/2" style={{ backgroundColor: preset.secondary }} />
                {/* Check de selección */}
                {isSelected && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Check className="h-4 w-4 text-white drop-shadow-md" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selectores individuales */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Color primario */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="primaryColor" className="text-sm text-content-2">
              Color Principal
            </label>
            <button
              type="button"
              onClick={() => setShowCustomPrimary(!showCustomPrimary)}
              className="text-xs text-accent hover:text-accent/75 flex items-center gap-1"
            >
              <Edit className="h-3 w-3" />
              {showCustomPrimary ? 'Ocultar' : 'Personalizar'}
            </button>
          </div>
          <div
            className="h-10 rounded-lg border border-line cursor-pointer flex items-center justify-center gap-2 transition-all hover:shadow-md"
            style={{ backgroundColor: primaryColor }}
            onClick={() => setShowCustomPrimary(!showCustomPrimary)}
          >
            <span className="text-xs font-medium" style={{ color: primaryTextColor }}>
              {primaryColor}
            </span>
          </div>
          {showCustomPrimary && (
            <div className="mt-2 flex items-center gap-2">
              <input
                type="color"
                id="primaryColor"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="w-10 h-10 p-0.5 rounded border border-line cursor-pointer"
              />
              <input
                type="text"
                value={primaryColor}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) setPrimaryColor(val);
                }}
                className="flex-1 text-sm border border-line rounded-md px-3 py-2 font-mono"
                placeholder="#000000"
                maxLength={7}
              />
            </div>
          )}
        </div>

        {/* Color secundario */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="secondaryColor" className="text-sm text-content-2">
              Color Secundario
            </label>
            <button
              type="button"
              onClick={() => setShowCustomSecondary(!showCustomSecondary)}
              className="text-xs text-accent hover:text-accent/75 flex items-center gap-1"
            >
              <Edit className="h-3 w-3" />
              {showCustomSecondary ? 'Ocultar' : 'Personalizar'}
            </button>
          </div>
          <div
            className="h-10 rounded-lg border border-line cursor-pointer flex items-center justify-center gap-2 transition-all hover:shadow-md"
            style={{ backgroundColor: secondaryColor }}
            onClick={() => setShowCustomSecondary(!showCustomSecondary)}
          >
            <span
              className="text-xs font-medium"
              style={{ color: isLightColor(secondaryColor) ? '#1e293b' : '#ffffff' }}
            >
              {secondaryColor}
            </span>
          </div>
          {showCustomSecondary && (
            <div className="mt-2 flex items-center gap-2">
              <input
                type="color"
                id="secondaryColor"
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                className="w-10 h-10 p-0.5 rounded border border-line cursor-pointer"
              />
              <input
                type="text"
                value={secondaryColor}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) setSecondaryColor(val);
                }}
                className="flex-1 text-sm border border-line rounded-md px-3 py-2 font-mono"
                placeholder="#FFFFFF"
                maxLength={7}
              />
            </div>
          )}
        </div>
      </div>

      {/* Preview en vivo */}
      <div className="border border-line rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-surface-2 border-b border-line">
          <p className="text-xs font-medium text-content-3 flex items-center gap-1">
            <Star className="h-3 w-3" />
            Vista previa
          </p>
        </div>
        <div className="p-4" style={{ backgroundColor: secondaryColor }}>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            {/* Mini kiosk card — siempre claro: imita al kiosco real, no al tema del manager */}
            <div className="bg-white rounded-xl shadow-md p-4 w-full max-w-[220px]">
              <div
                className="h-2 rounded-full mb-3"
                style={{ backgroundColor: primaryColor }}
              />
              <p className="text-sm font-bold text-slate-800 mb-1">
                {businessName || 'Mi Negocio'}
              </p>
              <p className="text-xs text-slate-400 mb-3">¡Bienvenido!</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="flex-1 text-xs font-medium py-2 px-3 rounded-lg transition-colors"
                  style={{
                    backgroundColor: primaryColor,
                    color: primaryTextColor,
                  }}
                >
                  Registrar
                </button>
                <button
                  type="button"
                  className="flex-1 text-xs font-medium py-2 px-3 rounded-lg border transition-colors"
                  style={{
                    borderColor: primaryColor,
                    color: primaryColor,
                  }}
                >
                  Consultar
                </button>
              </div>
            </div>

            {/* Mini navigation preview — siempre claro, igual que el kiosco */}
            <div className="bg-white rounded-xl shadow-md p-3 w-full max-w-[180px]">
              <div className="space-y-1.5">
                <div
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs font-medium"
                  style={{
                    backgroundColor: `${primaryColor}15`,
                    color: primaryColor,
                  }}
                >
                  <div
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: primaryColor }}
                  />
                  Inicio
                </div>
                <div className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-slate-400">
                  <div className="w-3 h-3 rounded bg-slate-200" />
                  Clientes
                </div>
                <div className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-slate-400">
                  <div className="w-3 h-3 rounded bg-slate-200" />
                  Recompensas
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorPickerSection;